/**
 * GoogleLoginButton — Direct Google OAuth 2.0 popup flow.
 *
 * UX :
 *   - Cliquer ouvre IMMÉDIATEMENT la popup officielle de sélection de compte Google
 *   - Aucune redirection intermédiaire (ni page de proxy)
 *   - Après succès, on échange le `code` côté backend qui pose un cookie de session
 *   - L'utilisateur est ensuite redirigé vers `next` (par défaut /dashboard)
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * Si le backend renvoie `enabled=false` via /api/auth/google/config (cas dev local
 * sans GOOGLE_CLIENT_ID), on retombe sur le fallback Emergent legacy.
 */
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useState } from 'react';

const InnerButton = ({ className, children, next, dataTestId, onSuccess }) => {
  const { exchangeGoogleCode } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const loginPopup = useGoogleLogin({
    flow: 'auth-code',
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    redirect_uri: typeof window !== 'undefined' ? `${window.location.origin}/auth/google` : undefined,
    ux_mode: 'popup',
    scope: 'openid email profile',
    onSuccess: async ({ code }) => {
      setBusy(true);
      try {
        const result = await exchangeGoogleCode({
          code,
          // Must match the redirect_uri sent above EXACTLY (Google checks)
          redirect_uri: `${window.location.origin}/auth/google`,
        });
        toast.success(`Bienvenue ${result?.user?.name || ''}`.trim());
        if (onSuccess) onSuccess(result);
        else navigate(next || '/hub/addresses');
      } catch (e) {
        const msg = e?.response?.data?.detail || 'Échec de la connexion Google';
        toast.error(msg);
        // eslint-disable-next-line no-console
        console.error('[google-oauth] exchange failed', e);
      } finally {
        setBusy(false);
      }
    },
    onError: (err) => {
      // eslint-disable-next-line no-console
      console.warn('[google-oauth] popup error', err);
      toast.error('Connexion Google annulée');
    },
  });

  return (
    <button
      type="button"
      onClick={() => loginPopup()}
      disabled={busy}
      data-testid={dataTestId || 'google-login-button'}
      className={className}
    >
      {busy ? 'Connexion…' : children}
    </button>
  );
};

/**
 * Public wrapper. Renders nothing if Google direct flow isn't enabled on the
 * backend (which lets the legacy Emergent button take over).
 */
const GoogleLoginButton = ({ className, children, next, dataTestId, onSuccess, fallback = null }) => {
  const { googleConfig } = useAuth();
  if (!googleConfig?.enabled || !googleConfig?.client_id) {
    return fallback;
  }
  return (
    <GoogleOAuthProvider clientId={googleConfig.client_id}>
      <InnerButton
        className={className}
        next={next}
        dataTestId={dataTestId}
        onSuccess={onSuccess}
      >
        {children}
      </InnerButton>
    </GoogleOAuthProvider>
  );
};

export default GoogleLoginButton;
